import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Link2, X, Plus, Users, FolderOpen, SlidersHorizontal, Eye, Building2, HardDrive, ExternalLink } from "lucide-react";
import AgencyWorkspaceManager from "@/components/AgencyWorkspaceManager";
import AgencySharedDocuments from "@/components/AgencySharedDocuments";
import { openAgencyPortalPreview } from "@/lib/agencyPortalPreview";
import { useProjectsPickerQuery } from "@/hooks/usePortalQueries";

// Staff-side control for a Partner Agency record: link projects to the agency (the sharing scope)
// and assign/unassign agency users to those projects. The agency's my-projects + cost-sheet
// visibility both key off these memberships (agency_project_memberships).
export default function AgencyAccessPanel({ agencyRemoteId, loginEmails, agencyName }) {
  const [linked, setLinked] = useState([]);
  const { data: allProjects = [] } = useProjectsPickerQuery();
  const [loading, setLoading] = useState(true);
  const [pickPid, setPickPid] = useState("");
  const [projSearch, setProjSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [openWorkspace, setOpenWorkspace] = useState({});
  const [linkedClient, setLinkedClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientLoading, setClientLoading] = useState(true);
  const [driveFolder, setDriveFolder] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [driveBusy, setDriveBusy] = useState(false);
  const [linkFolderInput, setLinkFolderInput] = useState("");
  const [showLinkFolder, setShowLinkFolder] = useState(false);

  const emails = (loginEmails || "").split(",").map((e) => e.trim().toLowerCase()).filter(Boolean);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/v1/build/agency/${agencyRemoteId}/projects`);
      setLinked(r.data.linked || []);
    } catch { /* */ } finally { setLoading(false); }
  }, [agencyRemoteId]);

  const reloadLinkedClient = useCallback(async () => {
    try {
      const r = await api.get(`/v1/build/agency/${agencyRemoteId}/linked-client-projects`);
      setLinkedClient(r.data.client || null);
      setClientProjects(r.data.projects || []);
    } catch { /* */ } finally { setClientLoading(false); }
  }, [agencyRemoteId]);

  const reloadDriveWorkspace = useCallback(async () => {
    try {
      const r = await api.get(`/v1/build/agency/${agencyRemoteId}/drive-workspace`);
      setDriveFolder(r.data.agency_drive_folder || null);
    } catch { /* */ } finally { setDriveLoading(false); }
  }, [agencyRemoteId]);

  useEffect(() => {
    reload();
    reloadLinkedClient();
    reloadDriveWorkspace();
  }, [reload, reloadLinkedClient, reloadDriveWorkspace]);

  const linkedIds = new Set(linked.map((l) => l.project_id));
  const unlinked = allProjects
    .filter((p) => !linkedIds.has(p.id))
    .filter((p) => !projSearch.trim() || (p.name || "").toLowerCase().includes(projSearch.trim().toLowerCase()));

  const linkProject = async () => {
    if (!pickPid) return;
    setBusy("link");
    try {
      await api.post(`/v1/build/agency/${agencyRemoteId}/projects/link`, { project_ids: [pickPid] });
      setPickPid("");
      toast.success("Project shared with agency");
      await reload();
    } catch { toast.error("Couldn't link project"); } finally { setBusy(""); }
  };

  const unlinkProject = async (pid, name) => {
    setBusy(`unlink-${pid}`);
    try {
      await api.delete(`/v1/build/agency/${agencyRemoteId}/projects/${pid}`);
      toast.success(`Stopped sharing "${name}"`);
      await Promise.all([reload(), reloadLinkedClient()]);
    } catch { toast.error("Couldn't unlink project"); } finally { setBusy(""); }
  };

  const giveClientProjectAccess = async (pid) => {
    setBusy(`give-${pid}`);
    try {
      await api.post(`/v1/build/agency/${agencyRemoteId}/projects/link`, { project_ids: [pid] });
      toast.success("Project shared with agency");
      await Promise.all([reload(), reloadLinkedClient()]);
    } catch { toast.error("Couldn't link project"); } finally { setBusy(""); }
  };

  const createDriveWorkspace = async () => {
    setDriveBusy(true);
    try {
      const r = await api.post(`/v1/build/agency/${agencyRemoteId}/drive-workspace`);
      setDriveFolder(r.data.agency_drive_folder);
      toast.success(r.data.created ? "Drive workspace created" : "Drive workspace already exists");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "partner_agencies_drive_root_not_configured"
        ? "Set the Partner Agencies Root Folder in Settings first" : "Couldn't create Drive workspace");
    } finally { setDriveBusy(false); }
  };

  const linkExistingDriveFolder = async () => {
    if (!linkFolderInput.trim()) return;
    setDriveBusy(true);
    try {
      const r = await api.post(`/v1/build/agency/${agencyRemoteId}/drive-workspace/link`, { folder_url_or_id: linkFolderInput.trim() });
      setDriveFolder(r.data.agency_drive_folder);
      setLinkFolderInput("");
      setShowLinkFolder(false);
      toast.success("Drive workspace linked");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "folder_not_in_partner_agencies_root" ? "That folder isn't directly inside the Partner Agencies root"
        : code === "not_a_folder" ? "That isn't a Drive folder" : "Couldn't link that folder");
    } finally { setDriveBusy(false); }
  };

  const assignMember = async (pid, email) => {
    if (!email) return;
    setBusy(`assign-${pid}`);
    try {
      await api.post(`/v1/build/agency/${agencyRemoteId}/members`, { member_email: email, project_ids: [pid] });
      toast.success("User assigned");
      await reload();
    } catch { toast.error("Couldn't assign user"); } finally { setBusy(""); }
  };

  const unassignMember = async (pid, email) => {
    setBusy(`unassign-${pid}-${email}`);
    try {
      await api.delete(`/v1/build/agency/memberships/${agencyRemoteId}:${email}:${pid}`);
      toast.success("User unassigned");
      await reload();
    } catch { toast.error("Couldn't unassign user"); } finally { setBusy(""); }
  };

  const assignAllDefault = async () => {
    setBusy("all");
    try {
      const r = await api.post(`/v1/build/agency/${agencyRemoteId}/assign-all-default`);
      toast.success(`Assigned ${r.data.members} user(s) to ${r.data.projects} project(s)`);
      await reload();
    } catch { toast.error("Couldn't run assign-all"); } finally { setBusy(""); }
  };

  return (
    <section className="rounded-lg border border-hairline bg-paper" data-testid="agency-access-panel">
      <header className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-3.5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-faint" />
          <h2 className="text-sm font-semibold text-ink" data-testid="agency-access-panel-title">What {agencyName || "this agency"} can sign in to</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => openAgencyPortalPreview(agencyRemoteId, agencyName || "this agency")}
            data-testid="agency-preview-as-agency-btn"
            className="inline-flex items-center gap-1.5 text-xs rounded-md border border-hairline px-2.5 py-1.5 text-ink hover:bg-black/5">
            <Eye className="h-3.5 w-3.5" /> Preview as Agency
          </button>
          {linked.length > 0 && emails.length > 0 && (
            <button onClick={assignAllDefault} disabled={busy === "all"} data-testid="agency-assign-all-default-btn"
              className="text-xs rounded-md border border-hairline px-2.5 py-1.5 text-ink hover:bg-black/5 disabled:opacity-50">
              {busy === "all" ? "Assigning…" : "Assign all users to all projects"}
            </button>
          )}
        </div>
      </header>

      <div className="p-5 space-y-5">
        {emails.length === 0 && (
          <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
            Add a login email to this agency record first, then you can assign users to projects.
          </div>
        )}

        {/* Linked Client — Trifilm/agency data model (Wave 1): this agency's Notion "Linked
           Client" relation, and that client's own projects, so staff can give agency access
           in one click instead of hunting for them in the global project search below. */}
        <div className="rounded-md border border-hairline p-3.5" data-testid="agency-linked-client-section">
          <label className="mb-2 block text-[11px] uppercase tracking-wider text-faint">Linked Client</label>
          {clientLoading ? (
            <div className="text-sm text-faint">Loading…</div>
          ) : !linkedClient ? (
            <div className="flex items-center gap-2 text-sm text-faint" data-testid="agency-no-linked-client">
              <Building2 className="h-3.5 w-3.5" /> No Linked Client set on this Partner Network record.
            </div>
          ) : (
            <div>
              <Link to={`/clients/${linkedClient.id}`} data-testid="agency-linked-client-name"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline">
                <Building2 className="h-3.5 w-3.5" /> {linkedClient.name}
              </Link>
              {clientProjects.length === 0 ? (
                <div className="mt-2 text-[11px] text-faint" data-testid="agency-linked-client-no-projects">This client has no projects yet.</div>
              ) : (
                <div className="mt-2.5 space-y-1.5">
                  {clientProjects.map((p) => (
                    <div key={p.project_id} data-testid={`agency-linked-client-project-${p.project_id}`}
                      className="flex items-center justify-between gap-3 rounded-md bg-black/[0.03] px-3 py-2">
                      <div>
                        <span className="text-sm text-ink">{p.name}</span>
                        {p.status && <span className="ml-2 text-[11px] text-faint">{p.status}</span>}
                        {p.archived && <span className="ml-2 text-[10px] uppercase text-red-600">archived</span>}
                      </div>
                      {p.linked ? (
                        <span className="text-[11px] text-emerald-600" data-testid={`agency-linked-client-project-linked-badge-${p.project_id}`}>Linked</span>
                      ) : (
                        <button onClick={() => giveClientProjectAccess(p.project_id)} disabled={busy === `give-${p.project_id}`}
                          data-testid={`agency-give-access-btn-${p.project_id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-1 text-[11px] text-ink hover:bg-black/5 disabled:opacity-50">
                          <Plus className="h-3 w-3" /> {busy === `give-${p.project_id}` ? "Giving access…" : "Give agency access"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drive Workspace — Wave 2: an NJM-internal Drive folder per agency ({Partner Name}/
           Shared Documents, {Partner Name}/Projects). The agency itself is NEVER given Drive
           access — Wave 3 will stream files through the app instead. */}
        <div className="rounded-md border border-hairline p-3.5" data-testid="agency-drive-workspace-section">
          <label className="mb-2 block text-[11px] uppercase tracking-wider text-faint">Drive Workspace (NJM-internal only)</label>
          {driveLoading ? (
            <div className="text-sm text-faint">Loading…</div>
          ) : driveFolder ? (
            <a href={driveFolder} target="_blank" rel="noreferrer" data-testid="agency-drive-workspace-open-link"
              className="inline-flex items-center gap-1.5 text-sm text-ink hover:underline">
              <HardDrive className="h-3.5 w-3.5" /> Open in Drive <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button onClick={createDriveWorkspace} disabled={driveBusy} data-testid="agency-create-drive-workspace-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs text-paper hover:bg-ink/90 disabled:opacity-50">
                  <HardDrive className="h-3.5 w-3.5" /> {driveBusy ? "Working…" : "Create Drive workspace"}
                </button>
                <button onClick={() => setShowLinkFolder((s) => !s)} data-testid="agency-show-link-folder-btn"
                  className="text-xs text-brand-dim hover:underline">Link existing folder instead</button>
              </div>
              {showLinkFolder && (
                <div className="flex items-end gap-2">
                  <input value={linkFolderInput} onChange={(e) => setLinkFolderInput(e.target.value)}
                    placeholder="Paste Drive folder URL or ID" data-testid="agency-link-folder-input"
                    className="flex-1 rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                  <button onClick={linkExistingDriveFolder} disabled={driveBusy || !linkFolderInput.trim()} data-testid="agency-link-folder-btn"
                    className="rounded-md border border-hairline px-3 py-2 text-xs text-ink hover:bg-black/5 disabled:opacity-50">
                    {driveBusy ? "Linking…" : "Link folder"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Agency-scope documents (Wave 3 final step) — cross-project, rooted at the agency's
           own Shared Documents Drive folder above. Same proxy/fail-closed-hide rules as the
           per-project Document Room inside AgencyWorkspaceManager. */}
        <AgencySharedDocuments agencyRemoteId={agencyRemoteId} />

        {/* Link a project */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-faint">Share a project with this agency</label>
            <input value={projSearch} onChange={(e) => setProjSearch(e.target.value)} placeholder="Search projects by name…"
              data-testid="agency-link-project-search"
              className="mb-2 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
            <select value={pickPid} onChange={(e) => setPickPid(e.target.value)} data-testid="agency-link-project-select"
              className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink">
              <option value="">{projSearch.trim() ? `Select a project… (${unlinked.length} match)` : "Select a project…"}</option>
              {unlinked.slice(0, 50).map((p) => <option key={p.id} value={p.id}>{p.name || "Untitled project"}</option>)}
            </select>
          </div>
          <button onClick={linkProject} disabled={!pickPid || busy === "link"} data-testid="agency-link-project-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs text-paper hover:bg-ink/90 disabled:opacity-50">
            <Link2 className="h-3.5 w-3.5" /> Link
          </button>
        </div>

        {/* Linked projects */}
        {loading ? (
          <div className="text-sm text-faint">Loading…</div>
        ) : linked.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-dashed border-hairline px-4 py-6 text-sm text-faint" data-testid="agency-no-linked-projects">
            <FolderOpen className="h-4 w-4" /> No projects shared with this agency yet.
          </div>
        ) : (
          <div className="space-y-3">
            {linked.map((l) => {
              const assignable = emails.filter((e) => !l.members.includes(e));
              return (
                <div key={l.project_id} className="rounded-md border border-hairline p-3.5" data-testid={`agency-linked-project-${l.project_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-ink">{l.name}{l.archived && <span className="ml-2 text-[10px] uppercase text-red-600">archived</span>}</div>
                      {l.status && <div className="text-[11px] text-faint">{l.status}</div>}
                    </div>
                    <button onClick={() => unlinkProject(l.project_id, l.name)} disabled={busy === `unlink-${l.project_id}`}
                      data-testid={`agency-unlink-project-${l.project_id}`} title="Stop sharing this project"
                      className="text-[11px] text-red-600 hover:underline disabled:opacity-50">Unlink</button>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {l.members.length === 0 && <span className="text-[11px] text-faint">No users assigned — agency can't see it yet.</span>}
                    {l.members.map((em) => (
                      <span key={em} data-testid={`agency-member-${em}-${l.project_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[11px] text-ink">
                        {em}
                        <button onClick={() => unassignMember(l.project_id, em)} data-testid={`agency-unassign-${em}-${l.project_id}`}
                          className="text-faint hover:text-red-600" title="Unassign"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>

                  {assignable.length > 0 && (
                    <div className="mt-2.5 flex items-center gap-2">
                      <select defaultValue="" data-testid={`agency-assign-member-select-${l.project_id}`}
                        onChange={(e) => { if (e.target.value) { assignMember(l.project_id, e.target.value); e.target.value = ""; } }}
                        className="rounded-md border border-hairline bg-paper px-2.5 py-1.5 text-xs text-ink">
                        <option value="">+ Assign a user…</option>
                        {assignable.map((em) => <option key={em} value={em}>{em}</option>)}
                      </select>
                      {busy === `assign-${l.project_id}` && <span className="text-[11px] text-faint">Assigning…</span>}
                    </div>
                  )}

                  <button onClick={() => setOpenWorkspace((s) => ({ ...s, [l.project_id]: !s[l.project_id] }))}
                    data-testid={`agency-workspace-toggle-${l.project_id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-brand-dim hover:underline">
                    <SlidersHorizontal className="h-3 w-3" /> {openWorkspace[l.project_id] ? "Hide" : "Manage"} agency workspace (open items &amp; documents)
                  </button>
                  {openWorkspace[l.project_id] && <AgencyWorkspaceManager projectId={l.project_id} agencyRemoteId={agencyRemoteId} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
